import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import eventTracker from '../analytics/EventTracker';
import { useCookieConsent } from '../components/CookieContext';

// Controllo globale per evitare duplicazioni
let globalLastTrackedPath = '';

// Hook per il tracking automatico di pageview (solo da usare in Layout)
export const useAnalytics = () => {
  const location = useLocation();
  const { isAccepted, isInitialized } = useCookieConsent();
  const lastTrackedPath = useRef('');

  // Pageview automatico su cambio route (solo se analytics è accettato)
  useEffect(() => {
    if (isAccepted('analytics') && isInitialized) {
      const currentPath = location.pathname + location.search;
      
      // Controllo locale e globale per evitare duplicazioni
      if (lastTrackedPath.current !== currentPath && globalLastTrackedPath !== currentPath) {
        lastTrackedPath.current = currentPath;
        globalLastTrackedPath = currentPath;
        
        // Usa il metodo pubblico di EventTracker per il pageview
        eventTracker.trackCustomEvent('page_view', {
          page_title: document.title,
          page_location: window.location.href,
          page_path: currentPath
        });
      }
    }
  }, [location.pathname, location.search, isAccepted('analytics'), isInitialized]);

  // Espone tutti i metodi di EventTracker
  return eventTracker;
};

// Hook per eventi specifici (da usare nei componenti)
export const useEventTracking = () => {
  const { isAccepted, isInitialized } = useCookieConsent();
  
  // Wrapper per verificare se il tracking è disponibile
  const canTrack = () => isAccepted('analytics') && isInitialized;
  
  // Metodi wrapper che controllano il consenso
  const trackEvent = (eventName, parameters = {}) => {
    if (canTrack()) {
      eventTracker.trackCustomEvent(eventName, parameters);
    }
  };
  
  const trackProductView = (productData) => {
    if (canTrack()) {
      eventTracker.trackProductView(productData);
    }
  };
  
  const trackProductClick = (productData, position) => {
    if (canTrack()) {
      eventTracker.trackProductClick(productData, position);
    }
  };
  
  const trackExternalClick = (productData, sellerData) => {
    if (canTrack()) {
      eventTracker.trackExternalClick(productData, sellerData);
    }
  };
  
  const trackWishlistAdd = (productData) => {
    if (canTrack()) {
      eventTracker.trackWishlistAdd(productData);
    }
  };
  
  const trackWishlistRemove = (productData) => {
    if (canTrack()) {
      eventTracker.trackWishlistRemove(productData);
    }
  };
  
  const trackSearch = (searchTerm, resultsCount) => {
    if (canTrack()) {
      eventTracker.trackSearch(searchTerm, resultsCount);
    }
  };
  
  const trackFilter = (filterType, filterValue) => {
    if (canTrack()) {
      eventTracker.trackFilter(filterType, filterValue);
    }
  };
  
  const trackCategoryView = (categoryData, page, items) => {
    if (canTrack()) {
      eventTracker.trackCategoryView(categoryData, page, items);
    }
  };
  
  const trackSellerView = (sellerData) => {
    if (canTrack()) {
      eventTracker.trackSellerView(sellerData);
    }
  };
  
  const trackAuth = (action, method, success, userType, error) => {
    if (canTrack()) {
      eventTracker.trackAuth(action, method, success, userType, error);
    }
  };
  
  const trackNavigation = (from, to, elementType) => {
    if (canTrack()) {
      eventTracker.trackNavigation(from, to, elementType);
    }
  };

  return {
    canTrack,
    trackEvent,
    trackProductView,
    trackProductClick,
    trackExternalClick,
    trackWishlistAdd,
    trackWishlistRemove,
    trackSearch,
    trackFilter,
    trackCategoryView,
    trackSellerView,
    trackAuth,
    trackNavigation
  };
}; 